import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import Typewriter from "typewriter-effect";

import arrow from "../images/arrow_svg.svg";
import arrowdiag from "../images/arrow_diag_svg.svg";

import Magnet from "../components/helpers/Magnet";
import { projects } from "../assets/projects";
import { bootcamps } from "../assets/bootcamps";

import { usePreload } from "../components/helpers/preloadMedia";

import "../App.css";

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const text = "REINIS";

function Home() {
  const [displayedText, setDisplayedText] = useState(text);
  const [hoveredRow, setHoveredRow] = useState(null); // cld store id with isVideo or other info rather
  const hoverAreaRef = useRef(null);
  const [showHoverContent, setShowHoverContent] = useState(false);
  const cursorPos = useRef({ x: 0, y: 0 });
  const navigate = useNavigate();
  const { isFontsReady } = usePreload();

  useEffect(() => {
    const track = (e) => {
      cursorPos.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", track);
    return () => window.removeEventListener("mousemove", track);
  }, []);

  useEffect(() => {
    setShowHoverContent(false);

    if (!hoveredRow) return;

    const delay = setTimeout(() => {
      setShowHoverContent(true);
    }, 200);

    return () => clearTimeout(delay);
  }, [hoveredRow]);

  useEffect(() => {
    if (!showHoverContent) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(
        new MouseEvent("mousemove", {
          clientX: cursorPos.current.x,
          clientY: cursorPos.current.y,
          bubbles: true,
        }),
      );
    }, 100);
    return () => clearTimeout(timer);
  }, [showHoverContent]);

  const handleRowClick = (id) => {
    if (id === 2) {
      navigate(`/projects/${id}`);
    }
    if (id === 4) {
      navigate(`/projects/${id}`);
    }
  };

  useEffect(() => {
    if (!isFontsReady) return;

    let iteration = 0;
    const textLength = text.length;

    const animate = setInterval(() => {
      setDisplayedText((prev) =>
        prev
          .split("")
          .map((char, index) =>
            index < Math.floor(iteration)
              ? text[index]
              : letters[Math.floor(Math.random() * letters.length)],
          )
          .join(""),
      );

      if (iteration >= textLength) {
        clearInterval(animate);
      }

      iteration += 0.5;
    }, 30);

    return () => {
      clearInterval(animate);
    };
  }, [isFontsReady]);

  const mediaFiles =
    projects.find((project) => project.id === hoveredRow)?.mediaFiles || [];
  const [image, rightImage, middleImage] = mediaFiles;

  const hoverContent = useMemo(() => {
    if (!showHoverContent) return null;
    return (
      <>
        {/* Left Image */}
        <div
          className={`hover-image ${hoveredRow ? "visible" : ""} ${hoveredRow === 1 ? "spread" : ""}`}
        >
          <Magnet magnetStrength={3}>
            <img src={image} alt="cover" id={`leftimg-${hoveredRow}`} />
          </Magnet>
        </div>

        {/* Middle Content */}
        <div className={`hover-image-middle ${hoveredRow ? "visible" : ""}`}>
          <Magnet magnetStrength={5}>
            {hoveredRow === 1 || hoveredRow === 10 ? (
              <img
                src={middleImage}
                alt="middle"
                id={`middleimg-${hoveredRow}`}
              />
            ) : (
              <video
                id={`middlevideo-${hoveredRow}`}
                src={middleImage}
                autoPlay
                muted
                loop
                preload="metadata"
                playsInline
                style={{ visibility: "visible" }}
              />
            )}
          </Magnet>
        </div>

        {/* Right Image */}
        <div
          className={`hover-image-right ${hoveredRow ? "visible" : ""} ${hoveredRow === 1 ? "spread" : ""}`}
        >
          <Magnet magnetStrength={3}>
            <img
              src={rightImage}
              id={`rightimg-${hoveredRow}`}
              alt="right"
              loading="lazy"
              style={{ visibility: "visible" }}
            />
          </Magnet>
        </div>
      </>
    );
  }, [showHoverContent, image, middleImage, rightImage, hoveredRow]);

  return (
    <>
      <div className="landing-page">
        <section id="landing">
          <div className="landing-name">
            <h1>{displayedText}</h1>
            <h2>MUIZNIEKS</h2>
          </div>
          <div className="landing-bio">
            <div className="corners">
              <div className="corner-shape top-left"></div>
              <div className="corner-shape top-right"></div>
            </div>
            <Typewriter
              onInit={(typewriter) => {
                typewriter
                  .changeDelay(30)
                  .typeString(
                    "Student, currently studying graphic design and web development.",
                  )
                  .pauseFor(1500)
                  .typeString(
                    " Also specialize in Full-Stack project development, Microsoft Power-Apps and UI Design.",
                  )
                  .start();
              }}
            />
            <div className="corners">
              <div className="corner-shape bottom-left"></div>
              <div className="corner-shape bottom-right"></div>
            </div>
          </div>
        </section>
      </div>
      <Marquee>
        <div className="marquee">
          <div className="projects-heading">PROJECTS</div>
          <div className="projects-heading">PROJECTS</div>
          <div className="projects-heading">PROJECTS</div>
        </div>
      </Marquee>
      <section id="projects">
        <div className="projects">
          <table>
            <tbody>
              {projects.map(({ id, title, description, year }) => (
                <tr
                  key={id}
                  onMouseEnter={() => setHoveredRow(id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => handleRowClick(id)}
                >
                  <td>{id}</td>
                  <td>{title}</td>
                  <td className="project-desc">{description}</td>
                  <td>{year}</td>
                  <td>{">>>"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div ref={hoverAreaRef} className="table-row">
            {hoverContent}
          </div>
        </div>
      </section>
      <section id="about">
        <div className="about-section">
          <div className="about-left">
            <div className="about-heading">
              <h3>Bootcamps & Experience</h3>
              <img src={arrow} alt="Arrow Icon" />
            </div>
            <table>
              <tbody>
                {bootcamps.map(({ year, institution, program, duration }) => (
                  <tr key={year + program}>
                    <td>{year}</td>
                    <td>{institution}</td>
                    <td>{program}</td>
                    <td>{duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="socials-section">
              <div className="about-heading">
                <h3>Get In Touch</h3>
                <img src={arrow} alt="Arrow Icon" />
              </div>
              <div className="socials">
                <img src={arrowdiag} alt="Arrow Icon" />
                <a href="mailto:muizreinis@gmail.com">Mail</a>
              </div>
              <div className="socials">
                <img src={arrowdiag} alt="Arrow Icon" />
                <a
                  href="https://www.linkedin.com/in/reinis-muiznieks-7b9021299/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
          <div className="about-right">
            <div className="about-heading">
              <h3>About</h3>
              <img src={arrow} alt="Arrow Icon" />
            </div>
            <p>
              I am a student studying in the field of Web Development and
              Graphic Design. During high school, I attended a technical school
              and graduated with a qualification as a Programming Technician
              (professional secondary education). Aside from my school work I
              enjoy participating in many different events and boot camps that
              help me expand my knowledge. <br />
              <br />
              In my free time I love to listen to music and watch movies from
              which I also draw a lot of inspiration from.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
